import { useEffect, useRef, useState } from "react"
import { useConnection } from "wagmi"
import { SnsUserStatus } from "@/types/user"

interface UseAutoVerificationProps {
  isVerified: boolean
  isAuthLoading: boolean
  isFetchingUser: boolean
  isManualConnect: boolean
  verifyAccount: () => Promise<void>
  postAutoSignStarted: () => void
  hasAutoSignTriggered: boolean
  nonce?: { nonce: string } | null
  user?: any
  isProfileError?: boolean
}

export const useAutoVerification = ({
  isVerified,
  isAuthLoading,
  isFetchingUser,
  isManualConnect,
  verifyAccount,
  postAutoSignStarted,
  hasAutoSignTriggered,
  nonce,
  user,
  isProfileError,
}: UseAutoVerificationProps) => {
  const { isConnected, address: connectedAddress } = useConnection()
  const signingRef = useRef(false)
  const [isVerifying, setIsVerifying] = useState(false)

  // Auto-trigger signing only once across all tabs (one tab triggers, others skip via hasAutoSignTriggered)
  useEffect(() => {
    // Don't trigger if:
    // 1. Already verifying
    // 2. Already started in this tab (signingRef)
    // 3. Any tab already started auto-sign (hasAutoSignTriggered)
    // 4. Not manual connect
    // 5. Already verified
    // 6–11. (same as before)
    if (
      isVerifying ||
      signingRef.current ||
      hasAutoSignTriggered ||
      !isManualConnect ||
      isVerified ||
      isAuthLoading ||
      isFetchingUser ||
      !isConnected ||
      !connectedAddress ||
      !nonce ||
      isProfileError ||
      (user && user.status !== SnsUserStatus.Active)
    ) {
      return
    }

    postAutoSignStarted()
    signingRef.current = true
    setIsVerifying(true)

    ;(async () => {
      try {
        await verifyAccount()
      } catch (error) {
        console.error("Auto-verification failed:", error)
      } finally {
        setIsVerifying(false)
        signingRef.current = false
      }
    })()

    return () => {
      signingRef.current = false
    }
  }, [
    isConnected,
    isVerified,
    connectedAddress,
    isAuthLoading,
    isFetchingUser,
    isManualConnect,
    hasAutoSignTriggered,
    nonce,
    isVerifying,
    verifyAccount,
    postAutoSignStarted,
    user,
    isProfileError,
  ])

  // Reset signing ref when disconnected
  useEffect(() => {
    if (!isConnected) {
      signingRef.current = false
      setIsVerifying(false)
    }
  }, [isConnected])
}
